import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { OnboardingProvider } from "@/components/Onboarding";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import ForcePasswordPage from "@/pages/ForcePasswordPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import CGUReAcceptModal from "@/components/CGUReAcceptModal";
import DashboardPage from "@/pages/DashboardPage";
import AgentChatPage from "@/pages/AgentChatPage";
import InstallPrompt from "@/components/InstallPrompt";
import TableRondePage from "@/pages/TableRondePage";
import AdminPage from "@/pages/AdminPage";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import AdminUsagePage from "@/pages/AdminUsagePage";
import CGUPage from "@/pages/CGUPage";
import ProfilePage from "@/pages/ProfilePage";
import ConversationsPage from "@/pages/ConversationsPage";
import FavoritesPage from "@/pages/FavoritesPage";
import NotificationsPage from "@/pages/NotificationsPage";
import DeepResearchPage from "@/pages/DeepResearchPage";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AuthRoute({ children }) {
  const { user } = useAuth();
  if (user && !user.must_change_password) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();

  // CRITICAL: Detect Google OAuth callback (session_id in URL hash) synchronously
  // BEFORE any Route match or /auth/me call. Prevents race conditions with AuthProvider.
  // Read from useLocation().hash (reactive), NOT window.location.hash (not reactive after history.replaceState).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <OnboardingProvider>
      <CGUReAcceptModal />
      <Routes>
        <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/change-password" element={<ForcePasswordPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/agent/:agentType" element={<ProtectedRoute><AgentChatPage /></ProtectedRoute>} />
        <Route path="/reunion-equipage" element={<ProtectedRoute><TableRondePage /></ProtectedRoute>} />
        <Route path="/table-ronde" element={<Navigate to="/reunion-equipage" replace />} />
        <Route path="/deep-research" element={<ProtectedRoute><DeepResearchPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><AdminAnalyticsPage /></ProtectedRoute>} />
        <Route path="/admin/usage" element={<ProtectedRoute adminOnly><AdminUsagePage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/conversations" element={<ProtectedRoute><ConversationsPage /></ProtectedRoute>} />
        <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/cgu" element={<CGUPage />} />
        <Route path="/conditions" element={<CGUPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      <InstallPrompt />
    </OnboardingProvider>
  );
}

function App() {
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.hideOdysLoader === "function") {
      window.hideOdysLoader();
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
